// src/components/AdminManagement.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, authAPI } from '../services/api';
import { Link } from 'react-router-dom';

const AdminManagement: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'global-read' as 'admin' | 'super-admin' | 'global-read',
    display_name: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  // Only super-admin can access this component
  if (user?.role !== 'super-admin') {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You don't have permission to access the admin management section.</p>
        <Link to="/dashboard" style={{ color: '#007bff', textDecoration: 'none' }}>
          Return to Dashboard
        </Link>
      </div>
    );
  }

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      // Note: This would need to be implemented in the backend API
      // For now, we'll show a placeholder
      setUsers([]);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        // Update user logic would go here
        setSuccess('User updated successfully!');
      } else {
        // Create user logic would go here
        setSuccess('User created successfully!');
      }
      
      await loadUsers();
      setShowAddUserForm(false);
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'global-read',
        display_name: ''
      });
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role,
      display_name: user.display_name
    });
    setShowAddUserForm(true);
  };

  const handleDelete = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      // Delete user logic would go here
      setSuccess('User deleted successfully!');
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Navigation */}
      <nav style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <Link to="/dashboard" style={{ color: '#007bff', textDecoration: 'none', fontWeight: 'bold' }}>
              ← Back to Dashboard
            </Link>
            <span style={{ color: '#6c757d' }}>|</span>
            <Link to="/plants" style={{ color: '#007bff', textDecoration: 'none' }}>
              Plant Management
            </Link>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#6c757d' }}>Welcome, {user?.display_name || user?.username}</span>
            <span style={{ 
              padding: '4px 8px', 
              backgroundColor: '#dc3545', 
              color: 'white', 
              borderRadius: '4px', 
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              SUPER-ADMIN
            </span>
          </div>
        </div>
      </nav>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Admin Management</h2>
        <button 
          onClick={() => {
            setShowAddUserForm(true);
            setEditingUser(null);
            setFormData({
              username: '',
              email: '',
              password: '',
              role: 'global-read',
              display_name: ''
            });
          }}
          style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px' }}
        >
          Add New User
        </button>
      </div>

      {error && (
        <div style={{ color: 'red', marginBottom: '20px', padding: '10px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px' }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ color: 'green', marginBottom: '20px', padding: '10px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '4px' }}>
          {success}
        </div>
      )}

      {/* Add/Edit User Form */}
      {showAddUserForm && (
        <div style={{ 
          marginBottom: '30px', 
          padding: '20px', 
          backgroundColor: '#f8f9fa', 
          border: '2px solid #28a745', 
          borderRadius: '8px' 
        }}>
          <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label>Username *:</label>
                <input 
                  type="text" 
                  name="username" 
                  value={formData.username} 
                  onChange={handleInputChange} 
                  style={{ width: '100%', padding: '8px' }} 
                  required 
                />
              </div>
              <div>
                <label>Email *:</label>
                <input 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleInputChange} 
                  style={{ width: '100%', padding: '8px' }} 
                  required 
                />
              </div>
              <div>
                <label>Password {editingUser ? '(leave blank to keep current)' : '*'}:</label>
                <input 
                  type="password" 
                  name="password" 
                  value={formData.password} 
                  onChange={handleInputChange} 
                  style={{ width: '100%', padding: '8px' }} 
                  required={!editingUser}
                />
              </div>
              <div>
                <label>Role *:</label>
                <select 
                  name="role" 
                  value={formData.role} 
                  onChange={handleInputChange} 
                  style={{ width: '100%', padding: '8px' }} 
                  required
                >
                  <option value="global-read">Viewer (Read Only)</option>
                  <option value="admin">Admin</option>
                  <option value="super-admin">Super Admin</option>
                </select>
              </div>
              <div>
                <label>Display Name:</label>
                <input 
                  type="text" 
                  name="display_name" 
                  value={formData.display_name} 
                  onChange={handleInputChange} 
                  style={{ width: '100%', padding: '8px' }} 
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                type="submit" 
                disabled={loading}
                style={{ 
                  padding: '10px 20px', 
                  backgroundColor: '#007bff', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
              </button>
              <button 
                type="button" 
                onClick={() => {
                  setShowAddUserForm(false);
                  setEditingUser(null);
                }}
                style={{ 
                  padding: '10px 20px', 
                  backgroundColor: '#6c757d', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px' 
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      <div style={{ marginBottom: '20px' }}>
        <h3>System Users</h3>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>Loading users...</div>
        ) : users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
            No users found. User management API needs to be implemented in the backend.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '15px' }}>
            {users.map((user) => (
              <div key={user.id} style={{ 
                padding: '15px', 
                backgroundColor: 'white', 
                border: '1px solid #dee2e6', 
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h4 style={{ margin: '0 0 5px 0' }}>{user.display_name || user.username}</h4>
                  <div style={{ color: '#6c757d', fontSize: '14px' }}>
                    <div><strong>Username:</strong> {user.username}</div>
                    <div><strong>Email:</strong> {user.email}</div>
                    <div><strong>Role:</strong> 
                      <span style={{ 
                        padding: '2px 6px', 
                        backgroundColor: user.role === 'super-admin' ? '#dc3545' : user.role === 'admin' ? '#007bff' : '#28a745', 
                        color: 'white', 
                        borderRadius: '3px', 
                        fontSize: '12px',
                        marginLeft: '5px'
                      }}>
                        {user.role.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => handleEdit(user)}
                    style={{ padding: '5px 10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px' }}
                  >
                    Edit
                  </button>
                  {user.id !== user.id && ( // Don't allow deleting self
                    <button
                      onClick={() => handleDelete(user.id)}
                      style={{ padding: '5px 10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px' }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* System Information */}
      <div style={{ 
        marginTop: '30px', 
        padding: '20px', 
        backgroundColor: '#e9ecef', 
        borderRadius: '8px' 
      }}>
        <h3>System Information</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div>
            <strong>Database Status:</strong> Connected ✅
          </div>
          <div>
            <strong>Backend API:</strong> Running ✅
          </div>
          <div>
            <strong>Total Plants:</strong> {users.length} (placeholder)
          </div>
          <div>
            <strong>Active Users:</strong> {users.length} (placeholder)
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminManagement;
